import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { searchTranscriptions, searchViralContent } from '../lib/api';
import { TranscriptionResponse } from '../lib/types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Search as SearchIcon, FileText, TrendingUp, Filter, AlertCircle, ThumbsUp, Eye, Clock, Loader2, ArrowRight, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';
export function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialMode = searchParams.get('mode') as 'regular' | 'viral' || 'regular';
  const [query, setQuery] = useState(initialQuery);
  const [searchMode, setSearchMode] = useState<'regular' | 'viral'>(initialMode);
  const [results, setResults] = useState<TranscriptionResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minLikes, setMinLikes] = useState(50);
  const [minViews, setMinViews] = useState(1000);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const resultsPerPage = 10;
  // Update URL when search parameters change
  useEffect(() => {
    if (initialQuery) {
      performSearch(initialQuery, initialMode);
    }
  }, [initialQuery, initialMode]);
  const performSearch = async (searchQuery: string, mode: 'regular' | 'viral', pageNum: number = 1) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const offset = (pageNum - 1) * resultsPerPage;
      let searchResults: TranscriptionResponse[];
      if (mode === 'viral') {
        searchResults = await searchViralContent(searchQuery, minLikes, minViews, resultsPerPage + 1 // Request one extra to check if there are more
        );
      } else {
        searchResults = await searchTranscriptions(searchQuery, resultsPerPage + 1,
        // Request one extra to check if there are more
        offset);
      }
      // Check if there are more results
      setHasMore(searchResults.length > resultsPerPage);
      // Remove the extra item if we have more
      if (searchResults.length > resultsPerPage) {
        searchResults = searchResults.slice(0, resultsPerPage);
      }
      setResults(searchResults);
      setPage(pageNum);
    } catch (err) {
      console.error('Search error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while searching. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams();
    newParams.set('q', query);
    newParams.set('mode', searchMode);
    setSearchParams(newParams);
    performSearch(query, searchMode, 1);
  };
  const handleNextPage = () => {
    if (hasMore) {
      performSearch(query, searchMode, page + 1);
    }
  };
  const handlePrevPage = () => {
    if (page > 1) {
      performSearch(query, searchMode, page - 1);
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  return <div className="container py-8" data-id="element-2271">
      <div className="mb-8" data-id="element-2272">
        <h1 className="text-3xl font-bold mb-2" data-id="element-2273">Search Transcriptions</h1>
        <p className="text-muted-foreground" data-id="element-2274">
          Find content across all transcribed videos with our powerful search
        </p>
      </div>
      {/* Search Form */}
      <form onSubmit={handleSearch} className="mb-6" data-id="element-2275">
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-2276">
          <div className="relative flex-grow" data-id="element-2277">
            <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" data-id="element-2278" />
            <Input type="text" placeholder="Search for keywords, phrases, or topics..." value={query} onChange={e => setQuery(e.target.value)} className="pl-10" data-id="element-2279" />
          </div>
          <div className="flex gap-2" data-id="element-2280">
            <Button type="submit" disabled={isLoading || !query.trim()} className="min-w-[100px]" data-id="element-2281">
              {isLoading ? <Loader2 size={16} className="mr-2 animate-spin" data-id="element-2282" /> : <SearchIcon size={16} className="mr-2" data-id="element-2283" />}
              Search
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowFilters(!showFilters)} data-id="element-2284">
              <Filter size={16} className="mr-2" data-id="element-2285" />
              Filters
            </Button>
          </div>
        </div>
        {/* Search Modes and Filters */}
        <div className={cn('mt-4', !showFilters && 'sm:flex items-center justify-between')} data-id="element-2286">
          <div className="flex flex-wrap gap-2 mb-4 sm:mb-0" data-id="element-2287">
            <Button type="button" variant={searchMode === 'regular' ? 'default' : 'outline'} size="sm" onClick={() => setSearchMode('regular')} className="flex items-center" data-id="element-2288">
              <FileText size={14} className="mr-1" data-id="element-2289" />
              Regular Search
            </Button>
            <Button type="button" variant={searchMode === 'viral' ? 'default' : 'outline'} size="sm" onClick={() => setSearchMode('viral')} className="flex items-center" data-id="element-2290">
              <TrendingUp size={14} className="mr-1" data-id="element-2291" />
              Viral Content
            </Button>
          </div>
          {/* Advanced Filters (conditionally shown) */}
          {showFilters && <div className="bg-secondary/20 p-4 rounded-lg space-y-4" data-id="element-2292">
              <h3 className="font-medium mb-2 text-sm" data-id="element-2293">Advanced Filters</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2294">
                <div data-id="element-2295">
                  <label className="block text-sm mb-1" data-id="element-2296">Minimum Likes</label>
                  <div className="flex items-center" data-id="element-2297">
                    <ThumbsUp size={14} className="text-muted-foreground mr-2" data-id="element-2298" />
                    <Input type="number" min="0" value={minLikes} onChange={e => setMinLikes(Number(e.target.value))} disabled={searchMode !== 'viral'} className="max-w-[150px]" data-id="element-2299" />
                  </div>
                </div>
                <div data-id="element-2300">
                  <label className="block text-sm mb-1" data-id="element-2301">Minimum Views</label>
                  <div className="flex items-center" data-id="element-2302">
                    <Eye size={14} className="text-muted-foreground mr-2" data-id="element-2303" />
                    <Input type="number" min="0" value={minViews} onChange={e => setMinViews(Number(e.target.value))} disabled={searchMode !== 'viral'} className="max-w-[150px]" data-id="element-2304" />
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground" data-id="element-2305">
                Note: Engagement filters only apply to Viral Content search mode
              </p>
            </div>}
        </div>
      </form>
      {/* Search Results */}
      {error ? <Card className="border-destructive/20 bg-destructive/5" data-id="element-2306">
          <CardContent className="p-6" data-id="element-2307">
            <div className="flex items-start" data-id="element-2308">
              <AlertCircle className="text-destructive mr-3 mt-0.5" size={20} data-id="element-2309" />
              <div data-id="element-2310">
                <h3 className="font-medium mb-1" data-id="element-2311">Search Error</h3>
                <p className="text-muted-foreground" data-id="element-2312">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card> : isLoading ? <div className="space-y-4" data-id="element-2313">
          {[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse" data-id="element-2314">
              <CardContent className="p-4" data-id="element-2315">
                <div className="h-5 bg-secondary/60 rounded w-3/4 mb-3" data-id="element-2316"></div>
                <div className="h-3 bg-secondary/40 rounded w-1/2 mb-2" data-id="element-2317"></div>
                <div className="h-3 bg-secondary/40 rounded w-full mb-2" data-id="element-2318"></div>
                <div className="h-3 bg-secondary/40 rounded w-4/5" data-id="element-2319"></div>
              </CardContent>
            </Card>)}
        </div> : results.length > 0 ? <div data-id="element-2320">
          <p className="text-sm text-muted-foreground mb-4" data-id="element-2321">
            Showing {results.length} results for "{query}"
            {searchMode === 'viral' && ' (viral content)'}
          </p>
          <div className="space-y-4 mb-6" data-id="element-2322">
            {results.map(result => <Link key={result.task_id} to={`/app/transcriptions/${result.task_id}`} className="block" data-id="element-2323">
                <Card className="overflow-hidden hover:border-primary/50 hover:shadow-sm transition-all" data-id="element-2324">
                  <CardContent className="p-0" data-id="element-2325">
                    <div className="flex flex-col md:flex-row" data-id="element-2326">
                      {/* Thumbnail if available */}
                      {(result.thumbnail_url || result.thumbnail || result.square_thumbnail_url) && <div className="w-full md:w-48 h-32 flex-shrink-0" data-id="element-2327">
                          <img src={result.square_thumbnail_url || result.thumbnail_url || result.thumbnail} alt={result.title || 'Video thumbnail'} className="w-full h-full object-cover" data-id="element-2328" />
                        </div>}
                      <div className="p-4 flex-grow" data-id="element-2329">
                        <h3 className="font-medium mb-1 line-clamp-1" data-id="element-2330">
                          {result.title || 'Untitled Transcription'}
                        </h3>
                        {/* Show quote if available */}
                        {result.quote && <div className="mb-2 text-sm italic text-muted-foreground line-clamp-2" data-id="element-2331">
                            "{result.quote}"
                          </div>}
                        {/* Show TLDR if available */}
                        {result.tldr && result.tldr.length > 0 && <div className="mb-2" data-id="element-2332">
                            <div className="flex items-center gap-1 text-xs font-medium text-primary mb-1" data-id="element-2333">
                              <Sparkles size={12} data-id="element-2334" />
                              <span data-id="element-2335">Key points:</span>
                            </div>
                            <ul className="text-xs text-muted-foreground space-y-1 line-clamp-2" data-id="element-2336">
                              {result.tldr.slice(0, 2).map((point, i) => <li key={i} className="flex items-start" data-id="element-2337">
                                  <span className="mr-1" data-id="element-2338">•</span>
                                  <span className="line-clamp-1" data-id="element-2339">{point}</span>
                                </li>)}
                            </ul>
                          </div>}
                        {/* Metadata */}
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground mt-2" data-id="element-2340">
                          <span className="flex items-center" data-id="element-2341">
                            <Clock size={12} className="mr-1" data-id="element-2342" />
                            {formatDate(result.created_at)}
                          </span>
                          {result.view_count !== undefined && <span className="flex items-center" data-id="element-2343">
                              <Eye size={12} className="mr-1" data-id="element-2344" />
                              {result.view_count.toLocaleString()} views
                            </span>}
                          {result.like_count !== undefined && <span className="flex items-center" data-id="element-2345">
                              <ThumbsUp size={12} className="mr-1" data-id="element-2346" />
                              {result.like_count.toLocaleString()} likes
                            </span>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>)}
          </div>
          {/* Pagination */}
          {(page > 1 || hasMore) && <div className="flex justify-between items-center" data-id="element-2347">
              <Button variant="outline" onClick={handlePrevPage} disabled={page <= 1} size="sm" data-id="element-2348">
                <ChevronLeft size={16} className="mr-1" data-id="element-2349" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground" data-id="element-2350">Page {page}</span>
              <Button variant="outline" onClick={handleNextPage} disabled={!hasMore} size="sm" data-id="element-2351">
                Next
                <ChevronRight size={16} className="ml-1" data-id="element-2352" />
              </Button>
            </div>}
        </div> : query ? <Card className="border-muted" data-id="element-2353">
          <CardContent className="p-8 text-center" data-id="element-2354">
            <SearchIcon size={48} className="mx-auto mb-4 text-muted-foreground/60" data-id="element-2355" />
            <h3 className="text-lg font-medium mb-2" data-id="element-2356">No results found</h3>
            <p className="text-muted-foreground mb-6" data-id="element-2357">
              We couldn't find any transcriptions matching "{query}"
              {searchMode === 'viral' && ' with the current engagement filters'}
            </p>
            {searchMode === 'viral' && <Button variant="outline" onClick={() => {
          setSearchMode('regular');
          performSearch(query, 'regular');
        }} data-id="element-2358">
                Try Regular Search
              </Button>}
          </CardContent>
        </Card> : null}
    </div>;
}